import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, ShoppingCart, ZoomIn } from "lucide-react";
import { Link } from "react-router-dom";
import { createWhatsAppLink, createProductWhatsAppMessage } from "@/lib/whatsapp";
import ImageZoom from "./ImageZoom";

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  image: string;
  colors?: string[];
  sizes?: string[];
  stockQuantity: number;
  stockStatus: "available" | "low" | "out_of_stock";
  isOnDemand?: boolean;
  pagseguroLink?: string;
}

const stockLabels = {
  available: { text: "Em estoque", class: "stock-available" },
  low: { text: "Últimas unidades", class: "stock-low" },
  out_of_stock: { text: "Esgotado", class: "stock-out" },
};

const ProductCard = ({
  id,
  name,
  price,
  image,
  colors = [],
  sizes = [],
  stockQuantity,
  stockStatus,
  isOnDemand = false,
  pagseguroLink,
}: ProductCardProps) => {
  const [showImageZoom, setShowImageZoom] = useState(false);
  const isOutOfStock = !isOnDemand && (stockStatus === "out_of_stock" || stockQuantity <= 0);
  const stockInfo = stockLabels[stockStatus];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="product-card group"
    >
      {/* Image */}
      <div className="relative aspect-product overflow-hidden bg-muted group">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Zoom Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowImageZoom(true);
          }}
          className="absolute top-3 left-3 p-2 rounded-full bg-white/90 hover:bg-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
          title="Ampliar imagem"
        >
          <ZoomIn className="w-4 h-4 text-gray-900" />
        </button>
        
        {/* Stock Badge */}
        <div className="absolute top-3 right-3">
          {isOnDemand ? (
            <span className="badge-on-demand">Sob encomenda</span>
          ) : (
            <span className={stockInfo.class}>{stockInfo.text}</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-display text-lg font-semibold text-foreground mb-2 line-clamp-2">
          {name}
        </h3>

        <p className="text-2xl font-bold text-primary mb-4">
          R$ {price.toFixed(2).replace(".", ",")}
        </p>

        {/* Variations */}
        {(colors.length > 0 || sizes.length > 0) && (
          <div className="space-y-2 mb-4">
            {colors.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Cores:</span>
                <div className="flex gap-1">
                  {colors.slice(0, 4).map((color) => (
                    <span key={color} className="text-foreground/80">
                      {color}
                      {colors.indexOf(color) < colors.length - 1 && colors.indexOf(color) < 3 && ", "}
                    </span>
                  ))}
                  {colors.length > 4 && <span>+{colors.length - 4}</span>}
                </div>
              </div>
            )}
            {sizes.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Tamanhos:</span>
                <span className="text-foreground/80">{sizes.join(", ")}</span>
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-col gap-2">
          {isOutOfStock ? (
            <button
              disabled
              className="w-full flex items-center justify-center gap-2 bg-muted text-muted-foreground py-3 rounded-full font-medium cursor-not-allowed"
            >
              <ShoppingCart className="w-4 h-4" />
              Esgotado
            </button>
          ) : (
            <Link
              to={`/produto/${id}`}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-full font-medium transition-all hover:opacity-90"
            >
              <ShoppingCart className="w-4 h-4" />
              Ver detalhes
            </Link>
          )}
          <a
            href={createWhatsAppLink(createProductWhatsAppMessage(name, price))}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 border border-green-600 text-green-600 py-3 rounded-full font-medium transition-all hover:bg-green-50"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-3">
          Por ser artesanal, cada peça é única
        </p>
      </div>

      {/* Image Zoom Modal */}
      <ImageZoom
        images={[image]}
        currentIndex={0}
        open={showImageZoom}
        onClose={() => setShowImageZoom(false)}
        alt={name}
      />
    </motion.div>
  );
};

export default ProductCard;
